/**
 * Contextual snout-verb prompt.
 *
 * This is deliberately a small, text-only surface. The interaction system owns
 * what Rosalie can do; this class only presents the already-decided state.
 */

export interface PromptState {
  /** The verb the player could perform right now, or null for nothing in reach. */
  verb: 'nudge' | 'root' | 'observe' | 'call' | 'notes' | null;
  /** 0..1 for a hold in progress; ignored when verb is 'nudge'. */
  progress: number;
  /** True when the prompt must not be shown at all. */
  suppressed: boolean;
  /** True on a touch device: show the finger instruction, not the key. */
  touch: boolean;
}

const WORDS: Readonly<Record<Exclude<PromptState['verb'], null>, string>> = {
  nudge: 'NUDGE',
  root: 'ROOT',
  observe: 'WATCH',
  call: 'CALL',
  notes: 'NOTES',
};

const KEYS: Readonly<Record<Exclude<PromptState['verb'], null>, string>> = {
  nudge: 'E',
  root: 'E',
  observe: 'Q',
  call: 'F',
  notes: 'Tab',
};

/** A dependency-free, render-step-safe prompt for the currently reachable verb. */
export class VerbPrompt {
  #root: HTMLDivElement;
  #word: HTMLSpanElement;
  #key: HTMLSpanElement;
  #meter: HTMLDivElement;
  #fill: HTMLDivElement;
  #style: HTMLStyleElement;
  #lastState: PromptState | null = null;
  /** The last verb actually offered, held through the fade-out. See `update`. */
  #lastWord = '';
  #lastKey = '';
  #lastText = new Map<HTMLElement, string>();
  #lastStyle = new Map<HTMLElement, Map<string, string>>();
  #disposed = false;

  constructor() {
    const style = document.createElement('style');
    style.dataset.rosalieVerbPrompt = '';
    style.textContent = `
      /* z-index 4, deliberately BELOW the beat's fade overlay at 5. A prompt
         that survives the fade to black is a prompt floating on the closing
         image of the beat. Suppression covers the rest; this covers the case
         where the two disagree. */
      /* Not selectable, for the same reason as the touch buttons: this is a
         caption on a game, not a paragraph. pointer-events: none stops a tap
         landing ON it and does not stop a drag that started elsewhere from
         sweeping through it and highlighting the word NUDGE. */
      .rosalie-verb-prompt, .rosalie-verb-prompt * {
        -webkit-user-select: none; user-select: none; -webkit-touch-callout: none;
      }
      .rosalie-verb-prompt { position: fixed; left: 50%; bottom: calc(env(safe-area-inset-bottom) + 108px); z-index: 4; display: flex; flex-direction: column; align-items: center; pointer-events: none; transform: translateX(-50%); opacity: 0; transition: opacity 0.18s ease; }
      .rosalie-verb-prompt-line { font: 500 15px/1 system-ui, sans-serif; letter-spacing: 0.14em; text-transform: uppercase; color: rgba(230, 226, 221, 0.82); text-shadow: 0 1px 3px rgba(0,0,0,0.55); white-space: nowrap; }
      .rosalie-verb-prompt-key { display: inline-block; margin-left: 12px; padding: 2px 7px; border: 1px solid rgba(230,226,221,0.5); border-radius: 4px; font-size: 13px; }
      .rosalie-verb-prompt-meter { width: 78px; height: 2px; margin-top: 7px; background: rgba(230, 226, 221, 0.22); }
      .rosalie-verb-prompt-meter-fill { width: 0%; height: 100%; background: rgba(230, 226, 221, 0.85); }
    `;
    document.head.append(style);
    this.#style = style;

    const root = document.createElement('div');
    root.className = 'rosalie-verb-prompt';
    root.dataset.rosalieVerbPrompt = '';
    const line = document.createElement('div');
    line.className = 'rosalie-verb-prompt-line';
    const word = document.createElement('span');
    const key = document.createElement('span');
    key.className = 'rosalie-verb-prompt-key';
    line.append(word, key);
    const meter = document.createElement('div');
    meter.className = 'rosalie-verb-prompt-meter';
    const fill = document.createElement('div');
    fill.className = 'rosalie-verb-prompt-meter-fill';
    meter.append(fill);
    root.append(line, meter);
    document.body.append(root);

    this.#root = root;
    this.#word = word;
    this.#key = key;
    this.#meter = meter;
    this.#fill = fill;
  }

  update(state: PromptState): void {
    if (this.#disposed || this.#sameState(state, this.#lastState)) return;
    this.#lastState = { ...state };

    const verb = state.verb;
    const visible = !state.suppressed && verb !== null;
    const hold = verb === 'root' || verb === 'observe';
    // The word and the key are kept through a fade-out rather than cleared.
    //
    // Clearing them is the obvious thing and it looks wrong: the opacity
    // transition takes 0.18 s, so emptying the text on the same frame the verb
    // goes null makes the word vanish instantly and then fades an empty box.
    // What the eye should see is the whole prompt going away as one thing.
    const word = verb === null ? this.#lastWord : WORDS[verb];
    const key = verb === null ? this.#lastKey : KEYS[verb];
    if (verb !== null) {
      this.#lastWord = WORDS[verb];
      this.#lastKey = KEYS[verb];
    }
    const progress = Number.isFinite(state.progress)
      ? Math.max(0, Math.min(1, state.progress))
      : 0;

    this.#setText(this.#word, word);
    this.#setText(this.#key, key);
    this.#setStyle(this.#key, 'display', state.touch ? 'none' : 'inline-block');
    this.#setStyle(this.#meter, 'display', hold ? 'block' : 'none');
    this.#setStyle(this.#fill, 'width', `${progress * 100}%`);
    this.#setStyle(this.#root, 'opacity', visible ? '1' : '0');
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#root.remove();
    this.#style.remove();
  }

  #sameState(next: PromptState, previous: PromptState | null): boolean {
    return previous !== null
      && next.verb === previous.verb
      && next.progress === previous.progress
      && next.suppressed === previous.suppressed
      && next.touch === previous.touch;
  }

  #setText(element: HTMLElement, value: string): void {
    if (this.#lastText.get(element) === value) return;
    element.textContent = value;
    this.#lastText.set(element, value);
  }

  #setStyle(element: HTMLElement, property: string, value: string): void {
    let properties = this.#lastStyle.get(element);
    if (properties === undefined) {
      properties = new Map<string, string>();
      this.#lastStyle.set(element, properties);
    }
    if (properties.get(property) === value) return;
    element.style.setProperty(property, value);
    properties.set(property, value);
  }
}
